/**
 * CallScriptsTab — Section 9B of the agentic-AI improvements spec.
 * Read-only mirror of the Sales Dialer's call scripts. Reps reference
 * scripts here while studying / preparing; edits happen only in the
 * Sales Dialer (single source of truth).
 *
 * Live data: pulls from `/api/dialer/scripts` (any authenticated user
 * can read their own scripts + any published scripts in the workspace).
 * Empty state shows when a workspace has zero scripts authored yet.
 */
import React, { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, FileText, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

function fmtDate(iso) {
  if (!iso || typeof iso !== "string") return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

/**
 * Stitch the script `sections` array into a single readable body.
 * Each section is rendered as a heading + content block. Falls back
 * to `internal_notes` when the script has no structured sections.
 */
function buildBody(script) {
  const sections = Array.isArray(script?.sections) ? script.sections : [];
  if (sections.length === 0) return (script?.internal_notes || "").trim();
  return sections
    .map((s) => {
      const head = (s?.heading || s?.label || s?.title || "Section").toUpperCase();
      const body = (s?.body || s?.text || s?.content || "").trim();
      return `${head}\n${body}`.trim();
    })
    .join("\n\n");
}

/** Pull the display "product" badge from the various shapes the backend may return. */
function pickProduct(script) {
  if (Array.isArray(script?.products) && script.products.length) return script.products[0];
  if (typeof script?.product === "string") return script.product;
  return "—";
}

export default function CallScriptsTab() {
  const { token } = useAuth();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/dialer/scripts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(res.data) ? res.data : (res.data?.scripts || []);
      const normalized = rows.map((s) => ({
        id:         s.id,
        name:       s.name || "Untitled script",
        product:    pickProduct(s),
        specialty:  (Array.isArray(s.angles) && s.angles.length ? s.angles.join(", ") : (s.specialty || "All specialties")),
        purpose:    (s.internal_notes || "").split("\n")[0].slice(0, 140) || (s.angles?.join(" · ") || ""),
        author:     s.created_by_name || s.created_by || "—",
        updated:    fmtDate(s.updated_at) || fmtDate(s.created_at) || "",
        body:       buildBody(s),
        status:     s.status || "draft",
      }));
      setScripts(normalized);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load scripts");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return scripts;
    return scripts.filter((sc) => `${sc.name} ${sc.specialty} ${sc.product} ${sc.purpose}`.toLowerCase().includes(s));
  }, [search, scripts]);

  return (
    <div data-testid="call-scripts-tab">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="scripts-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search scripts by product, specialty, or keyword…"
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <FileText className="h-3.5 w-3.5" /> Read-only · authored in Sales Dialer · synced live
        </div>
      </div>

      {loading ? (
        <div data-testid="scripts-loading" className="text-center py-16 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading scripts…
        </div>
      ) : error ? (
        <div data-testid="scripts-error" className="text-center py-12 text-rose-600 text-sm">{error}</div>
      ) : filtered.length === 0 ? (
        <div data-testid="scripts-empty" className="text-center py-12 text-slate-500 text-sm">
          {scripts.length === 0
            ? "No call scripts authored yet. Open the Sales Dialer to create your first one."
            : "No scripts match your search."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((sc) => (
            <div key={sc.id} data-testid={`script-card-${sc.id}`} className="p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition cursor-pointer" onClick={() => setSelected(sc)}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-semibold text-sm text-slate-900">{sc.name}</div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold uppercase tracking-wide flex-shrink-0">{sc.product}</span>
              </div>
              {sc.purpose && <div className="text-xs text-slate-500 mb-2">{sc.purpose}</div>}
              <div className="flex items-center justify-between text-[11px] text-slate-400">
                <span>{sc.specialty}</span>
                <span>by {sc.author}{sc.updated ? ` · ${sc.updated}` : ""}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div data-testid="script-viewer" className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div className="w-full max-w-3xl max-h-[calc(100vh-48px)] bg-white rounded-2xl overflow-hidden flex flex-col">
            <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{selected.name}</h3>
                <div className="text-[11px] text-slate-500 mt-0.5">{selected.product} · {selected.specialty}{selected.purpose ? ` · ${selected.purpose}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { navigator.clipboard.writeText(selected.body || ""); toast.success("Script copied"); }} className="text-xs px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50 inline-flex items-center gap-1.5">
                  <Copy className="h-3 w-3" /> Copy
                </button>
                <button onClick={() => setSelected(null)} className="text-xs px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50">Close</button>
              </div>
            </header>
            <pre className="flex-1 overflow-auto p-6 text-[13px] text-slate-800 whitespace-pre-wrap font-mono leading-relaxed">{selected.body || "(empty script)"}</pre>
            <footer className="px-6 py-3 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
              <span>Author: <strong className="text-slate-700">{selected.author}</strong>{selected.updated ? ` · last updated ${selected.updated}` : ""}</span>
              <span>To edit, open this script in Sales Dialer.</span>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
